import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useClient } from '../context/ClientContext';
import { PlusCircle, Trash2, Sparkles, DollarSign, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ModuleUploadZone } from '../components/ModuleUploadZone';
import { PendingImports } from '../components/PendingImports';
import { useFilterState, dateRangeFromPreset, PRESETS } from '../hooks/useFilterState';

const CATEGORIES = [
  'Labour', 'Rent', 'Utilities', 'Supplies', 'Marketing',
  'Repairs & Maintenance', 'Professional Fees', 'Insurance',
  'Depreciation', 'Travel', 'Bank Charges', 'Licence & Permits',
  'Food & Beverage Costs', 'Other'
];

export const Expenses = () => {
  const { selectedClient, API } = useClient();
  const [expenses, setExpenses] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    category: 'Labour', sub_category: '', vendor: '',
    amount: '', description: ''
  });

  // Persisted filters (date preset + category) — survive tab navigation
  const [filters, setFilters] = useFilterState('expenses', selectedClient?.id, {
    preset: 'all', start: '', end: '', category: ''
  });

  const load = async () => {
    if (!selectedClient) return;
    setLoading(true);
    try {
      const { start, end } = dateRangeFromPreset(filters.preset, filters.start, filters.end);
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      if (filters.category) params.category = filters.category;
      const res = await axios.get(`${API}/expenses/${selectedClient.id}`, { params });
      setExpenses(res.data.expenses);
      setByCategory(res.data.by_category);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedClient, filters]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/expenses/${selectedClient.id}`, {
        ...form, amount: parseFloat(form.amount)
      });
      setForm({ ...form, amount: '', description: '', vendor: '', sub_category: '' });
      load();
    } catch (err) { alert('Failed: ' + (err?.response?.data?.detail || err.message)); }
  };

  const removeRow = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await axios.delete(`${API}/expenses/${id}`);
    load();
  };

  if (!selectedClient) return <div className="p-8 text-text-muted">Select a client to view expenses.</div>;

  // Human-readable label for the active period
  const rangeLabel = (() => {
    if (filters.preset === 'all') return 'all time';
    if (filters.preset === 'custom') {
      if (filters.start && filters.end) return `${filters.start} → ${filters.end}`;
      return 'custom range';
    }
    const found = PRESETS.find(p => p.value === filters.preset);
    return found ? found.label.toLowerCase() : 'all time';
  })();

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary">Operating Expenses</h1>
          <p className="text-text-secondary text-sm mt-1">
            Track labour, rent, utilities &amp; all non-COGS costs for <strong>{selectedClient.name}</strong>
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider text-text-muted">Total ({rangeLabel})</p>
          <p className="text-2xl font-bold text-primary" data-testid="expense-total">₹{total.toLocaleString()}</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-border rounded-md p-3 flex flex-wrap items-end gap-3" data-testid="expense-filter-bar">
        <div className="flex items-center text-text-secondary">
          <Filter className="h-4 w-4 mr-1" />
          <span className="text-xs uppercase tracking-wider font-semibold">Filter</span>
        </div>
        <FilterField label="Period">
          <select
            value={filters.preset}
            onChange={e => setFilters({ ...filters, preset: e.target.value })}
            className="border border-border rounded px-2 py-1.5 text-sm bg-white"
            data-testid="filter-preset"
          >
            {PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </FilterField>
        {filters.preset === 'custom' && (
          <>
            <FilterField label="From">
              <input type="date" value={filters.start}
                onChange={e => setFilters({ ...filters, start: e.target.value })}
                className="border border-border rounded px-2 py-1.5 text-sm bg-white" data-testid="filter-custom-start" />
            </FilterField>
            <FilterField label="To">
              <input type="date" value={filters.end}
                onChange={e => setFilters({ ...filters, end: e.target.value })}
                className="border border-border rounded px-2 py-1.5 text-sm bg-white" data-testid="filter-custom-end" />
            </FilterField>
          </>
        )}
        <FilterField label="Category">
          <select
            value={filters.category}
            onChange={e => setFilters({ ...filters, category: e.target.value })}
            className="border border-border rounded px-2 py-1.5 text-sm bg-white"
            data-testid="filter-category"
          >
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </FilterField>
        {(filters.preset !== 'all' || filters.category) && (
          <Button variant="ghost" size="sm" onClick={() => setFilters({ preset: 'all', start: '', end: '', category: '' })}
            className="text-text-muted hover:text-state-error" data-testid="filter-clear">
            Clear
          </Button>
        )}
        <div className="ml-auto text-xs text-text-muted">
          Showing <strong className="text-primary">{expenses.length}</strong> {expenses.length === 1 ? 'expense' : 'expenses'}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {byCategory.slice(0, 6).map(c => (
          <div key={c.category} className="bg-white border border-border rounded-md p-3" data-testid={`expense-cat-${c.category}`}>
            <p className="text-[10px] uppercase tracking-wider text-text-muted font-semibold truncate">{c.category}</p>
            <p className="text-lg font-bold text-primary">₹{c.amount.toLocaleString()}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="manual" className="space-y-6">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="manual" data-testid="tab-manual-expense">Manual Entry</TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-upload-expense">
            <Sparkles className="h-3 w-3 mr-1 text-state-info" /> AI Upload Excel/PDF
          </TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list-expense">All Expenses ({expenses.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="manual">
          <form onSubmit={submit} className="bg-white border border-border rounded-md p-5 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="expense-form">
            <Input label="Date" type="date" value={form.date} onChange={v => setForm({...form, date: v})} testid="exp-date" />
            <SelectInput label="Category" value={form.category} options={CATEGORIES} onChange={v => setForm({...form, category: v})} testid="exp-category" />
            <Input label="Sub-category" value={form.sub_category} onChange={v => setForm({...form, sub_category: v})} testid="exp-sub" placeholder="e.g., Electricity" />
            <Input label="Vendor" value={form.vendor} onChange={v => setForm({...form, vendor: v})} testid="exp-vendor" />
            <Input label="Amount (₹)" type="number" value={form.amount} onChange={v => setForm({...form, amount: v})} testid="exp-amount" required />
            <Input label="Description" value={form.description} onChange={v => setForm({...form, description: v})} testid="exp-desc" />
            <div className="md:col-span-3 flex justify-end">
              <Button type="submit" data-testid="exp-submit">
                <PlusCircle className="h-4 w-4 mr-1" /> Add Expense
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="upload" className="space-y-4">
          <ModuleUploadZone module="expenses" moduleLabel="Operating Expense Statement"
            clientId={selectedClient.id} API={API} onUploadComplete={load} />
          <PendingImports clientId={selectedClient.id} API={API} module="expenses" onChange={load} />
        </TabsContent>

        <TabsContent value="list">
          <div className="bg-white border border-border rounded-md overflow-hidden">
            {loading ? <div className="p-8 text-center text-text-muted">Loading...</div> :
              expenses.length === 0 ?
                <div className="p-12 text-center text-text-muted">
                  <DollarSign className="h-12 w-12 mx-auto opacity-30 mb-3" />
                  No expenses yet. Add manually or upload an expense statement.
                </div>
              :
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Vendor</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Source</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {expenses.map(e => (
                      <tr key={e.id} data-testid={`expense-row-${e.id}`}>
                        <td className="px-4 py-3 text-sm">{e.date}</td>
                        <td className="px-4 py-3 text-sm">{e.category}{e.sub_category ? ` · ${e.sub_category}` : ''}</td>
                        <td className="px-4 py-3 text-sm">{e.vendor || '—'}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary truncate max-w-xs">{e.description || '—'}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold">₹{e.amount.toLocaleString()}</td>
                        <td className="px-4 py-3 text-center text-[10px] uppercase font-semibold">
                          <span className={`px-1.5 py-0.5 rounded ${e.source === 'manual' ? 'bg-blue-100 text-state-info' : 'bg-purple-100 text-purple-700'}`}>
                            {e.source}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button onClick={() => removeRow(e.id)} variant="ghost" size="sm" className="text-state-error hover:bg-red-50" data-testid={`delete-exp-${e.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Input = ({ label, value, onChange, type = 'text', testid, placeholder, required }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold">{label}{required && ' *'}</span>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      required={required} step="any"
      className="w-full mt-1 border border-border rounded px-3 py-2 text-sm bg-white" data-testid={testid} />
  </label>
);

const FilterField = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-0.5 block">{label}</span>
    {children}
  </label>
);

const SelectInput = ({ label, value, options, onChange, testid }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold">{label}</span>
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full mt-1 border border-border rounded px-3 py-2 text-sm bg-white" data-testid={testid}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

export default Expenses;
